import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import type {
  InvalidBetRow,
  InvalidBetsQuery,
  InvalidBetsResult,
  InvalidBetsTab,
} from '@/features/logs/invalidBetsTypes'

/** CM `POST /v1/bet/getVARBets` / `POST /v1/invalid-bet-log/searchIBLogs` request. */
export type InvalidBetLogsRequest = {
  page?: number
  pageSize?: number
  fromDate?: string
  toDate?: string
  member?: string
  playerName?: string | string[]
  /** Sportwise filter — Lab sport name; omit when All. */
  sport?: string
}

/** One CM-API invalid / VAR bet log row. */
export type InvalidBetLogApiRow = {
  id: string
  playerName?: string
  userName?: string
  event?: string
  placedAt?: string
  placedDate?: string
  updatedAt?: string
  type?: string
  selectionName?: string
  odds?: number | string
  betRate?: number | string
  stake?: number | string
  invalidBy?: string
  placeIp?: string
  IP?: string
  invalidIp?: string
  invalidMemberIp?: string
}

/** Standard API Response from CM-API (`strix-api-json-io`). */
export type InvalidBetLogsResponse = {
  status: number
  message: string
  data: {
    dataArray: InvalidBetLogApiRow[]
    totalCount: number
    limit: number
    noOfPages?: number
    page: number
  }
}

export function isInvalidBetLogsOk(
  live: InvalidBetLogsResponse | null | undefined,
): live is InvalidBetLogsResponse {
  return live?.status === 200 && live.data != null
}

/** Lab Sportsbook `Bet/getVARBets` → CM-API shared DB. */
export async function getVARBets(
  body: InvalidBetLogsRequest = {},
): Promise<InvalidBetLogsResponse> {
  return cmApi.getVARBets(authHeaders(), toRequestBody(body))
}

/** Lab User `InvalidBetLog/searchIBLogs` → CM-API shared DB. */
export async function searchIBLogs(
  body: InvalidBetLogsRequest = {},
): Promise<InvalidBetLogsResponse> {
  return cmApi.searchIBLogs(authHeaders(), toRequestBody(body))
}

function toRequestBody(body: InvalidBetLogsRequest): Record<string, unknown> {
  const out: Record<string, unknown> = {
    page: body.page ?? 1,
    pageSize: body.pageSize ?? DEFAULT_PAGE_SIZE,
  }
  if (body.fromDate) out.fromDate = body.fromDate
  if (body.toDate) out.toDate = body.toDate

  const sport = body.sport?.trim()
  if (sport && sport.toLowerCase() !== 'all') out.sport = sport

  const fromPlayerName = normalizePlayerNames(body.playerName)
  const fromMember = body.member?.trim()
  const names =
    fromPlayerName.length > 0 ? fromPlayerName : fromMember ? [fromMember] : []

  if (names.length === 1) {
    out.member = names[0]
    out.playerName = names[0]
  } else if (names.length > 1) {
    out.playerName = names
  }
  return out
}

function normalizePlayerNames(raw: string | string[] | undefined): string[] {
  if (raw == null) return []
  const list = Array.isArray(raw) ? raw : [raw]
  return [
    ...new Set(
      list
        .map((v) => String(v ?? '').trim())
        .filter((v) => v && v.toLowerCase() !== 'all'),
    ),
  ]
}

/** Map CM rows → console Invalid Bets table. */
export function toInvalidBetsResult(
  live: InvalidBetLogsResponse,
  query: InvalidBetsQuery,
): InvalidBetsResult {
  const pageSize = live.data?.limit ?? query.pageSize ?? DEFAULT_PAGE_SIZE
  const page = live.data?.page ?? query.page ?? 1
  const total = live.data?.totalCount ?? 0
  const tab: InvalidBetsTab = query.tab
  const rows: InvalidBetRow[] = (live.data?.dataArray ?? []).map((row) =>
    mapRow(row, tab),
  )
  return { rows, total, page, pageSize, tab }
}

function mapRow(row: InvalidBetLogApiRow, tab: InvalidBetsTab): InvalidBetRow {
  const playerName = row.playerName ?? row.userName ?? ''
  const placeIp = row.placeIp ?? row.IP ?? ''
  const placedAt = row.placedAt ?? row.placedDate ?? ''
  const updatedAt = row.updatedAt ?? ''
  const base: InvalidBetRow = {
    id: String(row.id),
    playerName,
    placedAt,
    updatedAt,
    type: row.type ?? '',
    selectionName: row.selectionName ?? '',
    odds: row.odds ?? row.betRate ?? '',
    stake: row.stake ?? '',
    placeIp,
  }
  if (tab === 'var-bet') {
    return { ...base, event: row.event ?? '' }
  }
  return {
    ...base,
    invalidBy: row.invalidBy ?? '',
    invalidIp: row.invalidIp ?? row.invalidMemberIp ?? '',
  }
}
